import React, {
    useState, useEffect, useMemo,
} from 'react';

import { Button, Group, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    Col, Row, ToggleButton, ToggleButtonGroup,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import DataTable from './DataTable';
import useDataFetch from './hooks/useDataFetch';
import usePocketSchema from './schemas/usePocketSchema';
import { useQueryClient } from '../../contexts/queryClient';
import PocketForm from '../../screens/Pockets/PocketForm';
import dataUrlToFile from '../../tools/dataUrlToFile';
import uploadImage from '../../tools/uploadImage';
import { getPocket, storePocketData } from '../../utils/neurons';

const LANGS = ['ru', 'en'];

export default function PocketDataTable({
    type, project, columnsArray,
    filterFields, formFields,
    renderRightColumn, isFlex, noDrag,
}) {
    const [currentItem, setItem] = useState({});
    const [currentLang, setLang] = useState('ru');
    const [currentFilters, setFilters] = useState({});
    const [invalidFields, setInvalidFields] = useState([]);
    const [lastEditTime, setLastEditTime] = useState(null);
    const [isTableLoading, setTableLoading] = useState(false);

    const { queryClient } = useQueryClient();

    const id = `list-${type}`;

    const {
        data,
        queryKey,
        mutatation,
        refreshData,
        ...restProps
    } = useDataFetch({
        id,
        fetchFn: getPocket,
        fetchProps: { pocket: type, project },
        onMutate: handlePocketMutation,
        onMutationSuccess: handlePocketMutationSuccess,
    });

    useEffect(() => {
        filterItems();
        setLastEditTime(data?.results?.last_edited_at || data?.last_edited_at);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, currentFilters]);

    const pocketSchema = usePocketSchema({
        columns: columnsArray,
        project,
        currentItem,
        onDelete: removeItemFromRows,
        onEdit: editItem,
        onChange: handleTableChange,
    });

    const isMultilang = useMemo(() => formFields.some((field) => field.isMultilang), [formFields]);

    function removeDoubles(newRows) {
        return newRows.reduce((accumulator, item) => {
            if (!accumulator.find((uniqueItem) => uniqueItem.entity_id === item.entity_id)) {
                accumulator.push(item);
            }

            return accumulator;
        }, []);
    }

    function storeData(newRows) {
        const formattedData = removeDoubles(newRows);

        mutatation.mutate({
            mutationFn: storePocketData,
            mutationProps: {
                data: {
                    data: formattedData,
                    last_edited_at: lastEditTime,
                },
            },
        });
    }

    function handlePocketMutation(newData) {
        queryClient.setQueryData(queryKey, () => ({ results: newData.mutationProps.data.data }));
    }

    function handlePocketMutationSuccess() {
        // TODO: рефреш нужен, чтобы получить заголовок новой записи, надо это починить
        refreshData();

        notifications.show({
            title: 'Успешно сохранено 👍',
            color: 'green',
        });

        setTimeout(() => {
            setTableLoading(false);
        }, 100);
    }

    function saveItem() {
        const validation = validate();

        if (!validation.isValid) {
            let message = '';

            Object.entries(validation.fields).map(([lang, langData]) => {
                if (lang === 'multi' && langData?.length) {
                    message += `Не заполнены общие поля: ${langData.join(', ')}. `;
                } else if (langData?.length) {
                    message += `Для языка ${lang} не заполнены поля: ${langData.join(', ')}. `;
                }
            });

            setInvalidFields(validation.fields);

            toast.error(message);

            return;
        }

        addItemToRows(currentItem);
        setInvalidFields({});
    }

    function editItem(e) {
        const { id: itemId } = e.currentTarget.dataset;
        const clonedRows = JSON.parse(JSON.stringify(data?.results));

        const entityObj = clonedRows.find((item) => +item.id === +itemId);

        if (!entityObj) {
            return;
        }

        setItem(entityObj);
    }

    function addItemToRows(newRow) {
        setTableLoading(true);

        let clonedRows = JSON.parse(JSON.stringify(data?.results));

        if (newRow.id) {
            const editedItemIndex = clonedRows.findIndex((item) => item.id === newRow.id);

            clonedRows[editedItemIndex] = newRow;
        } else {
            if (isFlex) {
                newRow.id = Date.now();
            }

            clonedRows.push(newRow);

            clonedRows = sortRows(clonedRows, clonedRows.length - 1, 0);
        }

        resetItem();
        storeData(clonedRows);
    }

    function removeItemFromRows(id) {
        setTableLoading(true);
        let clonedRows = JSON.parse(JSON.stringify(data?.results));

        const deletedItemIndex = clonedRows.findIndex((item) => +item.id === +id);

        clonedRows = sortRows(clonedRows, deletedItemIndex, clonedRows.length - 1);

        clonedRows = clonedRows.slice(0, -1);

        storeData(clonedRows);
    }

    function resetItem() {
        setItem({});
    }

    function filterItems() {
        const filter = Object.entries(currentFilters);

        const keys = {};
        // добавления валидаторов для параметров разного типа данных
        filter.forEach(([key, value]) => {
            if (value instanceof Array) {
                keys[key] = {
                    rowValidator: (row) => !value?.length || value.some((rowVal) => row[key].some((filterVal) => rowVal.id === filterVal.id)),
                };
            } else if (typeof value === 'string' || typeof value === 'number') {
                keys[key] = {
                    rowValidator: (row) => !value || new Date(row[key]).toDateString() === new Date(value).toDateString(),
                };
            } else {
                keys[key] = { rowValidator: () => true };
            }
        });
    }

    function handleTableChange(e) {
        const { name, checked, dataset } = e.currentTarget;

        if (dataset.type?.includes('active')) {
            handleActive({
                id: name,
                checked,
                dataset,
            });
        }
    }

    function handleChange(e) {
        const { value, checked } = e.currentTarget;
        const isCheckbox = e.currentTarget.getAttribute && e.currentTarget.getAttribute('type') === 'checkbox';

        const newValue = isCheckbox ? checked : value;

        setItem({
            ...currentItem,
            [e.currentTarget.id]: newValue,
        });
    }

    function handleLangDataChange(e) {
        const newItem = JSON.parse(JSON.stringify(currentItem));

        newItem.content = {
            ...(newItem.content || []),
            [currentLang]: {
                ...((newItem.content && newItem.content[currentLang]) || []),
                [e.currentTarget.id]: e.currentTarget.value,
            },
        };

        setItem(newItem);
    }

    function handleDateChange(dateKey, date) {
        setItem({
            ...currentItem,
            [dateKey]: date,
        });
    }

    async function handleImageChange({ id, value, isMultilang }) {
        const file = await dataUrlToFile(value, `pocket-${Date.now()}`);
        const uploadedImage = await uploadImage(file);

        const newItem = JSON.parse(JSON.stringify(currentItem));

        if (isMultilang) {
            newItem.content = {
                ...(newItem.content || []),
                [currentLang]: {
                    ...((newItem.content && newItem.content[currentLang]) || []),
                    [id]: uploadedImage.url,
                },
            };
        } else {
            newItem[id] = uploadedImage.url;
        }

        setItem(newItem);
    }

    function handleFiltersChange(e) {
        setFilters({
            ...currentFilters,
            [e.currentTarget.id]: e.currentTarget.value,
        });
    }

    function handleFiltersDateChange(key, value) {
        setFilters({
            ...currentFilters,
            [key]: value?.toISOString(),
        });
    }

    function handleFiltersClear() {
        setFilters({});
    }

    function handleSearch(selectedItem) {
        setItem({
            alias: selectedItem.alias,
            entity_alias: selectedItem.alias,
            entity_id: selectedItem.value,
            value: selectedItem.value,
            title: selectedItem.label,
        });
    }

    function handleActive({ id: activeId, checked, dataset }) {
        const withSingleActive = dataset.type.includes('single');
        const clonedRows = JSON.parse(JSON.stringify(data?.results));

        for (let i = 0; i < clonedRows.length; i++) {
            if (+clonedRows[i].id === +activeId) {
                clonedRows[i][dataset.key] = checked;
            } else if (withSingleActive) {
                clonedRows[i][dataset.key] = false;
            }
        }

        storeData(clonedRows);
    }

    function dragEndHandle(e) {
        if (typeof e.destination?.index === 'undefined') {
            return;
        }

        setTableLoading(true);

        let clonedRows = JSON.parse(JSON.stringify(data?.results));

        clonedRows = sortRows(clonedRows, e.source.index, e.destination.index);

        storeData(clonedRows);
    }

    function sortRows(arr, oldIndex, newIndex) {
        let movableItems = structuredClone(arr);

        // Удаляем запись со старого индекса
        const movedItem = movableItems.splice(oldIndex, 1)[0];

        // Помещаем её по новому индексу
        movableItems.splice(newIndex, 0, movedItem);

        // Выносим отдельно все незалоченные записи
        movableItems = movableItems.filter((item) => !item.pos_locked);

        const sortedArray = [];
        arr.forEach((item) => {
            if (item.pos_locked) { // Кладём в новый массив залоченные из оригинального
                sortedArray.push(item);
            } else { // и незалоченные, которые уже отсортированы в нужном порядке
                sortedArray.push(movableItems.shift());
            }
        });

        return sortedArray;
    }

    function validate() {
        const emptyFields = {
            ru: [],
            en: [],
            multi: [],
        };

        formFields.map((field) => {
            if (field.isRequired) {
                if (field.isMultilang) {
                    LANGS.forEach((lang) => {
                        if (!currentItem.content?.[lang]?.[field.name]) {
                            emptyFields[lang].push(field.name);
                        }
                    });
                } else if (!currentItem[field.name]) {
                    emptyFields.multi.push(field.name);
                }
            }
        });

        return {
            isValid: !emptyFields.ru.length && !emptyFields.en.length && !emptyFields.multi.length,
            fields: emptyFields,
        };
    }

    return (
        <div className="container-fluid">
            <Row className="align-items-end mb-3 position-relative" style={{ zIndex: 2 }}>
                <Col md={7}>
                    <Row>
                        <PocketForm
                            direction="row"
                            fieldList={filterFields}
                            currentItem={currentFilters}
                            onChange={handleFiltersChange}
                            onDateChange={handleFiltersDateChange}
                            onClear={handleFiltersClear}
                            withClear={!!filterFields.length}
                        />
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col md={7}>
                    <DataTable
                        {...restProps}
                        schema={pocketSchema}
                        data={data}
                        isDraggable={!noDrag}
                        onDragEnd={dragEndHandle}
                        isFetching={isTableLoading}
                    />
                </Col>
                <Col md={5}>
                    <Stack>
                        <PocketForm
                            fieldList={formFields}
                            invalidFields={invalidFields}
                            currentItem={currentItem}
                            currentLang={currentLang}
                            onChange={handleChange}
                            onLangDataChange={handleLangDataChange}
                            onImageChange={handleImageChange}
                            onDateChange={handleDateChange}
                            onSearch={handleSearch}
                        />
                        <Group>
                            <Button
                                color="blue"
                                onClick={saveItem}
                            >
                                Сохранить
                            </Button>
                            <Button
                                variant="outline"
                                color="gray"
                                onClick={resetItem}
                            >
                                Отмена
                            </Button>
                        </Group>
                    </Stack>
                    <Row>
                        {isMultilang && (
                            <Col md={{ span: 1, offset: 5 }}>
                                <Row className="justify-content-md-end">
                                    <ToggleButtonGroup
                                        value={currentLang}
                                        name="lang"
                                        key="inlineStyleBlock"
                                        type="radio"
                                        onChange={setLang}
                                    >
                                        <ToggleButton variant="outline-secondary" value="ru"> Ru </ToggleButton>
                                        <ToggleButton variant="outline-secondary" value="en"> En </ToggleButton>
                                    </ToggleButtonGroup>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    {renderRightColumn && renderRightColumn()}
                </Col>
            </Row>
        </div>
    );
}

PocketDataTable.defaultProps = {
    formFields: [],
    filterFields: [],
    itemsLimit: 20,
    schema: [],
};
